import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { brand, fonts } from '../brand'
import { anim, heroLayout } from '../theme'
import { WebChatWidgetDemo } from './WebChatWidgetDemo'

/**
 * Hero público na identidade da marca-mãe Codout (codout.com): base clara monocromática,
 * Space Grotesk peso 500 com tracking apertado, H1 em gradiente tinta -> ciano, eyebrow em mono,
 * CTAs em pílula (tinta + ghost) e um grid técnico sutil ao fundo. À direita, o widget do produto
 * (o análogo do "terminal" do codout.com para este SaaS de suporte). Copy vinda do i18n.
 */
export function LandingHero() {
  const { t } = useTranslation()

  const industries = [
    t('landing.hero.industry1'),
    t('landing.hero.industry2'),
    t('landing.hero.industry3'),
    t('landing.hero.industry4'),
    t('landing.hero.industry5'),
  ]

  const trust = [
    t('landing.hero.trustNoCard'),
    t('landing.hero.trustFirstAgent'),
    t('landing.hero.trustIsolatedData'),
  ]

  return (
    <section
      style={{
        position: 'relative',
        overflow: 'hidden',
        background: brand.canvas,
        color: brand.ink,
        fontFamily: fonts.body,
      }}
    >
      {/* Grid técnico sutil ao fundo (motivo Codout) */}
      <div
        data-decor=""
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          backgroundImage: `linear-gradient(${brand.line} 1px, transparent 1px), linear-gradient(90deg, ${brand.line} 1px, transparent 1px)`,
          backgroundSize: '48px 48px',
          maskImage: 'radial-gradient(120% 90% at 70% 0%, #000 30%, transparent 78%)',
          WebkitMaskImage: 'radial-gradient(120% 90% at 70% 0%, #000 30%, transparent 78%)',
          opacity: 0.7,
        }}
      />

      <div
        style={{
          position: 'relative',
          maxWidth: 1200,
          margin: '0 auto',
          padding: '84px 24px 92px',
          display: 'grid',
          gap: 52,
          alignItems: 'center',
          gridTemplateColumns: heroLayout.gridTemplateColumns,
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 22,
            textAlign: heroLayout.textAlign,
            alignItems: heroLayout.alignItems,
          }}
        >
          <span
            style={{
              alignSelf: heroLayout.alignItems,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 9,
              fontFamily: fonts.mono,
              fontSize: 12,
              fontWeight: 500,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: brand.slate,
              animation: anim.fade,
            }}
          >
            <span
              data-decor=""
              aria-hidden="true"
              style={{
                width: 7,
                height: 7,
                borderRadius: 2,
                background: brand.signal,
                animation: anim.pulseSlow,
              }}
            />
            {t('landing.hero.badge')}
          </span>

          <h1
            style={{
              margin: 0,
              fontFamily: fonts.display,
              fontSize: 'clamp(38px, 5.6vw, 66px)',
              lineHeight: 1.02,
              fontWeight: 500,
              letterSpacing: '-0.035em',
              textWrap: 'balance',
              color: brand.ink,
              animation: anim.fade,
            }}
          >
            {t('landing.hero.titleLine1')}
            <br />
            <span
              style={{
                backgroundImage: brand.gradHi,
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                color: 'transparent',
              }}
            >
              {t('landing.hero.titleHighlight')}
            </span>
          </h1>

          <p
            style={{
              margin: 0,
              fontSize: 17.5,
              lineHeight: 1.55,
              color: brand.inkSoft,
              maxWidth: 520,
              textWrap: 'pretty',
              animation: anim.fade,
            }}
          >
            {t('landing.hero.subtitle')}
          </p>

          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 12,
              justifyContent: heroLayout.justifyContent,
              animation: anim.fade,
            }}
          >
            <Link
              to="/signup"
              style={{
                fontFamily: fonts.display,
                fontSize: 15,
                fontWeight: 500,
                padding: '13px 26px',
                borderRadius: 100,
                background: brand.ink,
                color: '#fff',
                boxShadow: brand.shadowLg,
              }}
            >
              {t('landing.hero.ctaPrimary')}
            </Link>
            <a
              href="#como"
              style={{
                fontFamily: fonts.display,
                fontSize: 15,
                fontWeight: 500,
                padding: '12px 24px',
                borderRadius: 100,
                border: `1px solid ${brand.lineStrong}`,
                color: brand.ink,
              }}
            >
              {t('landing.hero.ctaSecondary')}
            </a>
          </div>

          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '10px 22px',
              fontFamily: fonts.mono,
              fontSize: 12,
              color: brand.slate,
              justifyContent: heroLayout.justifyContent,
              animation: anim.fade,
            }}
          >
            {trust.map((item) => (
              <span key={item} style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                <span style={{ color: brand.live }} aria-hidden="true">
                  ✓
                </span>
                {item}
              </span>
            ))}
          </div>
        </div>

        <WebChatWidgetDemo />
      </div>

      <div style={{ position: 'relative', borderTop: `1px solid ${brand.line}` }}>
        <div
          style={{
            maxWidth: 1200,
            margin: '0 auto',
            padding: '22px 24px',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '14px 40px',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span
            style={{
              fontFamily: fonts.mono,
              fontSize: 12,
              fontWeight: 500,
              color: brand.slate,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
            }}
          >
            {t('landing.hero.industriesLabel')}
          </span>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '12px 28px',
              fontFamily: fonts.mono,
              fontSize: 12.5,
              color: brand.inkSoft,
            }}
          >
            {industries.map((industry, index) => (
              <span
                key={industry}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}
              >
                {index > 0 ? (
                  <span aria-hidden="true" style={{ color: brand.slate }}>
                    ·
                  </span>
                ) : null}
                {industry}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
